/**
 * TikTok Pixel Tracking Service
 * Handles TikTok pixel events with SHA-256 hashing for PII data
 */

declare global {
  interface Window {
    ttq: any;
  }
}

/**
 * SHA-256 hash function for PII data
 */
async function sha256Hash(value: string): Promise<string> {
  // Normalize the value (trim, lowercase)
  const normalized = value.trim().toLowerCase();

  // Use Web Crypto API for SHA-256 hashing
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
}

/**
 * TikTok Pixel Tracking Client
 */
class TikTokPixel {
  private initialized: boolean = false;

  constructor() {
    this.waitForTikTok();
  }

  /**
   * Wait for TikTok pixel to load
   */
  private waitForTikTok() {
    if (typeof window !== 'undefined' && window.ttq) {
      this.initialized = true;
      return;
    }

    // Poll for ttq availability
    const checkInterval = setInterval(() => {
      if (typeof window !== 'undefined' && window.ttq) {
        this.initialized = true;
        clearInterval(checkInterval);
      }
    }, 100);

    // Stop polling after 10 seconds
    setTimeout(() => clearInterval(checkInterval), 10000);
  }

  /**
   * Check if TikTok pixel is ready
   */
  private isReady(): boolean {
    return this.initialized && typeof window !== 'undefined' && window.ttq;
  }

  /**
   * Identify user with hashed PII data
   */
  async identify(params: {
    email?: string;
    phoneNumber?: string;
    externalId?: string;
  }) {
    if (!this.isReady()) {
      console.warn('[TikTok] Pixel not ready, skipping identify');
      return;
    }

    const identifyData: Record<string, string> = {};

    // Hash email if provided
    if (params.email) {
      identifyData.email = await sha256Hash(params.email);
    }

    // Hash phone number if provided
    if (params.phoneNumber) {
      identifyData.phone_number = await sha256Hash(params.phoneNumber);
    }

    // Hash external ID if provided
    if (params.externalId) {
      identifyData.external_id = await sha256Hash(params.externalId);
    }

    try {
      window.ttq.identify(identifyData);
      console.log('[TikTok] User identified');
    } catch (error) {
      console.error('[TikTok] Error identifying user:', error);
    }
  }

  /**
   * Track ViewContent event
   */
  trackViewContent(params: {
    contentId?: string;
    contentType?: string;
    contentName?: string;
    value?: number;
    currency?: string;
  }) {
    if (!this.isReady()) {
      console.warn('[TikTok] Pixel not ready, skipping ViewContent');
      return;
    }

    const eventData: Record<string, any> = {};

    // Build contents array with only defined fields
    const contentItem: Record<string, any> = {};
    if (params.contentId) {
      contentItem.content_id = params.contentId;
    }
    if (params.contentType) {
      contentItem.content_type = params.contentType;
    }
    if (params.contentName) {
      contentItem.content_name = params.contentName;
    }

    // Only add contents if we have at least content_id
    if (contentItem.content_id) {
      eventData.contents = [contentItem];
    }

    if (params.value !== undefined) {
      eventData.value = params.value;
    }

    if (params.currency) {
      eventData.currency = params.currency;
    }

    try {
      window.ttq.track('ViewContent', eventData);
      console.log('[TikTok] ViewContent tracked:', params.contentName);
    } catch (error) {
      console.error('[TikTok] Error tracking ViewContent:', error);
    }
  }

  /**
   * Track Search event
   */
  trackSearch(params: {
    searchString: string;
    contentId?: string;
    contentType?: string;
    contentName?: string;
    value?: number;
    currency?: string;
  }) {
    if (!this.isReady()) {
      console.warn('[TikTok] Pixel not ready, skipping Search');
      return;
    }

    const eventData: Record<string, any> = {
      search_string: params.searchString,
    };

    // Build contents array with only defined fields
    const contentItem: Record<string, any> = {};
    if (params.contentId) {
      contentItem.content_id = params.contentId;
    }
    if (params.contentType) {
      contentItem.content_type = params.contentType;
    }
    if (params.contentName) {
      contentItem.content_name = params.contentName;
    }

    // Only add contents if we have at least content_id
    if (contentItem.content_id) {
      eventData.contents = [contentItem];
    }

    if (params.value !== undefined) {
      eventData.value = params.value;
    }

    if (params.currency) {
      eventData.currency = params.currency;
    }

    try {
      window.ttq.track('Search', eventData);
      console.log('[TikTok] Search tracked:', params.searchString);
    } catch (error) {
      console.error('[TikTok] Error tracking Search:', error);
    }
  }

  /**
   * Track ClickButton event
   */
  trackClickButton(params: {
    contentId?: string;
    contentType?: string;
    contentName?: string;
    value?: number;
    currency?: string;
  }) {
    if (!this.isReady()) {
      console.warn('[TikTok] Pixel not ready, skipping ClickButton');
      return;
    }

    const eventData: Record<string, any> = {};

    // Build contents array with only defined fields
    const contentItem: Record<string, any> = {};
    if (params.contentId) {
      contentItem.content_id = params.contentId;
    }
    if (params.contentType) {
      contentItem.content_type = params.contentType;
    }
    if (params.contentName) {
      contentItem.content_name = params.contentName;
    }

    // Only add contents if we have at least content_id
    if (contentItem.content_id) {
      eventData.contents = [contentItem];
    }

    if (params.value !== undefined) {
      eventData.value = params.value;
    }

    if (params.currency) {
      eventData.currency = params.currency;
    }

    try {
      window.ttq.track('ClickButton', eventData);
      console.log('[TikTok] ClickButton tracked:', params.contentName);
    } catch (error) {
      console.error('[TikTok] Error tracking ClickButton:', error);
    }
  }

  /**
   * Track Lead event (signup, subscription, etc.)
   */
  trackLead(params: {
    contentId?: string;
    contentType?: string;
    contentName?: string;
    value?: number;
    currency?: string;
  }) {
    if (!this.isReady()) {
      console.warn('[TikTok] Pixel not ready, skipping Lead');
      return;
    }

    const eventData: Record<string, any> = {};

    // Build contents array with only defined fields
    const contentItem: Record<string, any> = {};
    if (params.contentId) {
      contentItem.content_id = params.contentId;
    }
    if (params.contentType) {
      contentItem.content_type = params.contentType;
    }
    if (params.contentName) {
      contentItem.content_name = params.contentName;
    }

    // Only add contents if we have at least content_id
    if (contentItem.content_id) {
      eventData.contents = [contentItem];
    }

    if (params.value !== undefined) {
      eventData.value = params.value;
    }

    if (params.currency) {
      eventData.currency = params.currency;
    }

    try {
      window.ttq.track('Lead', eventData);
      console.log('[TikTok] Lead tracked:', params.contentName);
    } catch (error) {
      console.error('[TikTok] Error tracking Lead:', error);
    }
  }

  /**
   * Track CompletePayment event
   */
  trackCompletePayment(params: {
    contentId?: string;
    contentType?: string;
    contentName?: string;
    value: number;
    currency: string;
  }) {
    if (!this.isReady()) {
      console.warn('[TikTok] Pixel not ready, skipping CompletePayment');
      return;
    }

    const eventData: Record<string, any> = {
      value: params.value,
      currency: params.currency,
    };

    // Build contents array with only defined fields
    const contentItem: Record<string, any> = {};
    if (params.contentId) {
      contentItem.content_id = params.contentId;
    }
    if (params.contentType) {
      contentItem.content_type = params.contentType;
    }
    if (params.contentName) {
      contentItem.content_name = params.contentName;
    }

    // Only add contents if we have at least content_id
    if (contentItem.content_id) {
      eventData.contents = [contentItem];
    }

    try {
      window.ttq.track('CompletePayment', eventData);
      console.log('[TikTok] CompletePayment tracked:', params.value, params.currency);
    } catch (error) {
      console.error('[TikTok] Error tracking CompletePayment:', error);
    }
  }
}

// Create singleton instance
export const tiktok = new TikTokPixel();

export default tiktok;
